package com.kinoshazam.history

import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavDestination.Companion.hasRoute
import androidx.navigation.NavHostController
import androidx.navigation.toRoute
import com.kinoshazam.model.Route
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

data class NavSnapshot(
    val main: List<Route>,
    val modal: List<Route>,
    /** Selected tab on the main screen. Kept even when another screen covers it. */
    val tab: String = TAB_HOME,
) {
    fun browserPath(): String {
        val top = modal.lastOrNull() ?: main.lastOrNull() ?: Route.Main
        if (top == Route.Main) return mainTabPath(tab)
        return top.toBrowserPath()
    }

    /**
     * Prefixes a cold open can walk with the browser back button:
     * main grows first, then the modal stack on top of that main stack.
     */
    fun linearTrail(): List<NavSnapshot> {
        val mainSteps = main.indices.map { index ->
            copy(main = main.take(index + 1), modal = emptyList())
        }.ifEmpty { listOf(copy(main = listOf(Route.Main), modal = emptyList())) }
        if (modal.isEmpty()) return mainSteps
        val base = main.ifEmpty { listOf(Route.Main) }
        val modalSteps = modal.indices.map { index ->
            copy(main = base, modal = modal.take(index + 1))
        }
        return mainSteps + modalSteps
    }

    companion object {
        fun home(): NavSnapshot = NavSnapshot(main = listOf(Route.Main), modal = emptyList())
    }
}

const val TAB_HOME = "home"
const val TAB_MOVIES = "movies"
const val TAB_SERIES = "series"
const val TAB_CARTOONS = "cartoons"
const val TAB_FAVORITES = "favorites"

internal fun mainTabPath(tab: String): String = when (tab) {
    TAB_MOVIES -> "/movies"
    TAB_SERIES -> "/series"
    TAB_CARTOONS -> "/cartoons"
    TAB_FAVORITES -> "/favorites"
    else -> "/"
}

internal fun isBackNavigation(from: NavSnapshot, to: NavSnapshot): Boolean {
    if (to == from) return false
    if (to.main.size > from.main.size || to.modal.size > from.modal.size) return false
    if (from.main.take(to.main.size) != to.main) return false
    if (from.modal.take(to.modal.size) != to.modal) return false
    return to.main.size < from.main.size || to.modal.size < from.modal.size
}

internal fun restorable(main: List<Route>, modal: List<Route>): NavSnapshot =
    NavSnapshot(
        main = main.filterNot { it is Route.Update }.ifEmpty { listOf(Route.Main) },
        modal = modal,
    )

/** Code entry and the update dialog do not survive a reload. */
internal fun NavSnapshot.withoutSessionScreens(): NavSnapshot = copy(
    main = main.filterNot { it is Route.Update }.ifEmpty { listOf(Route.Main) },
    modal = modal.filterNot { it is Route.Dialog.OtpScreen },
)

@Suppress("RestrictedApi")
internal fun NavHostController.appRoutes(): List<Route> =
    currentBackStack.value.mapNotNull { entry -> entry.appRoute() }

/**
 * Pops and pushes until this controller shows [target]. Shared prefix entries stay
 * so their view models are kept.
 */
internal fun NavHostController.align(target: List<Route>) {
    repeat(MAX_ALIGN_STEPS) {
        val current = appRoutes()
        if (current == target) return
        val prefix = current.zip(target).takeWhile { (have, want) -> have == want }.count()
        if (current.size > prefix) {
            if (!popBackStack()) return
            if (appRoutes() == current) return
            return@repeat
        }
        val next = target.getOrNull(current.size) ?: return
        navigate(next)
    }
}

internal fun encodeHistory(snapshot: NavSnapshot, depth: Int): String =
    historyJson.encodeToString(
        HistoryRecord.serializer(),
        HistoryRecord(
            main = snapshot.main.map { it.toStored() },
            modal = snapshot.modal.map { it.toStored() },
            depth = depth,
            tab = snapshot.tab,
        ),
    )

internal fun decodeHistory(json: String?): NavSnapshotRecord? {
    if (json.isNullOrBlank() || !json.trimStart().startsWith("{")) return null
    val record = runCatching {
        historyJson.decodeFromString(HistoryRecord.serializer(), json)
    }.getOrNull() ?: return null
    return NavSnapshotRecord(
        snapshot = NavSnapshot(
            main = record.main.map { it.toRoute() }.ifEmpty { listOf(Route.Main) },
            modal = record.modal.map { it.toRoute() },
            tab = record.tab.ifBlank { TAB_HOME },
        ),
        depth = record.depth.coerceAtLeast(0),
    )
}

internal data class NavSnapshotRecord(
    val snapshot: NavSnapshot,
    val depth: Int,
)

/**
 * Cold open from the address bar. The modal player also puts its title underneath,
 * because that is the screen a back press would return to.
 */
internal fun decodeBrowserPath(raw: String): NavSnapshot? {
    val path = raw.substringBefore('?').let { value ->
        if (value.length > 1) value.trimEnd('/') else value.ifBlank { "/" }
    }
    val query = parseQuery(raw.substringAfter('?', ""))
    val segments = path.split('/').filter { it.isNotEmpty() }
    when (segments.firstOrNull()) {
        null -> return NavSnapshot.home()
        "movies" -> return tabOrDetails(segments, serial = false, tab = TAB_MOVIES)
        "series" -> return tabOrDetails(segments, serial = true, tab = TAB_SERIES)
        "cartoons" -> return if (segments.size == 1) NavSnapshot.home().copy(tab = TAB_CARTOONS) else null
        "favorites" -> return if (segments.size == 1) NavSnapshot.home().copy(tab = TAB_FAVORITES) else null
        "title" -> {
            val id = segments.getOrNull(1)?.toIntOrNull() ?: return null
            val serial = query["serial"] == "1"
            return detailsSnapshot(id, serial, if (serial) TAB_SERIES else TAB_MOVIES)
        }
    }
    val route = decodeRoute(raw) ?: return null
    return when (route) {
        Route.Main -> NavSnapshot.home()
        is Route.Dialog.WebPlayer -> {
            val modal = buildList {
                if (route.videoId > 0) {
                    add(Route.Dialog.Details(id = route.videoId, isSerial = route.isTvShow))
                }
                add(route)
            }
            NavSnapshot(
                main = listOf(Route.Main),
                modal = modal,
                tab = if (route.isTvShow) TAB_SERIES else TAB_MOVIES,
            )
        }
        is Route.Dialog.Details -> detailsSnapshot(
            id = route.id,
            serial = route.isSerial,
            tab = if (route.isSerial) TAB_SERIES else TAB_MOVIES,
        )
        is Route.Dialog -> NavSnapshot(main = listOf(Route.Main), modal = listOf(route))
        else -> NavSnapshot(main = listOf(Route.Main, route), modal = emptyList())
    }
}

private fun tabOrDetails(segments: List<String>, serial: Boolean, tab: String): NavSnapshot? {
    if (segments.size == 1) return NavSnapshot.home().copy(tab = tab)
    val id = segments.getOrNull(1)?.toIntOrNull() ?: return null
    if (segments.size != 2) return null
    return detailsSnapshot(id, serial, tab)
}

private fun detailsSnapshot(id: Int, serial: Boolean, tab: String): NavSnapshot =
    NavSnapshot(
        main = listOf(Route.Main),
        modal = listOf(Route.Dialog.Details(id = id, isSerial = serial)),
        tab = tab,
    )

/**
 * The address is what the user refreshed. A saved snapshot must not move
 * `/favorites` (or another main tab) back to `/` because its tab field is home.
 */
internal fun NavSnapshot.preferTabFromPath(pathAndQuery: String): NavSnapshot {
    val decoded = decodeBrowserPath(pathAndQuery) ?: return this
    if (modal.isNotEmpty() || decoded.modal.isNotEmpty()) return this
    if (main.lastOrNull() != Route.Main || decoded.main.lastOrNull() != Route.Main) return this
    return copy(tab = decoded.tab)
}

internal fun isJoinLocation(pathAndQuery: String): Boolean {
    val path = pathAndQuery.substringBefore('?')
    val query = pathAndQuery.substringAfter('?', "")
    return path == "/join" || path.startsWith("/join/") || query.contains("join=")
}

internal fun embedPlayUrl(
    imdbId: String,
    isTvShow: Boolean,
    seasonNumber: Int,
    episodeNumber: Int,
): String {
    val id = imdbId.trim()
    if (id.isEmpty()) return ""
    return if (isTvShow) {
        "https://api.tobaco.ws/embed/imdb/$id?season=$seasonNumber&episode=$episodeNumber&host=filmot.site&episodesOpen=false"
    } else {
        "https://api.tobaco.ws/embed/imdb/$id?host=filmot.site"
    }
}

private const val MAX_ALIGN_STEPS = 64

private val historyJson = Json {
    ignoreUnknownKeys = true
    classDiscriminator = "t"
    encodeDefaults = true
}

@Serializable
private data class HistoryRecord(
    val main: List<StoredRoute>,
    val modal: List<StoredRoute>,
    val depth: Int = 0,
    val tab: String = TAB_HOME,
)

@Serializable
private sealed interface StoredRoute {
    @Serializable
    @SerialName("main")
    data object Main : StoredRoute

    @Serializable
    @SerialName("about")
    data object AppInfo : StoredRoute

    @Serializable
    @SerialName("history")
    data object History : StoredRoute

    @Serializable
    @SerialName("videos")
    data class AllVideo(
        val screenType: String,
        val title: String,
        val genre: String,
        val personId: Int = -1,
    ) : StoredRoute

    @Serializable
    @SerialName("player")
    data class VideoPlayer(val url: String) : StoredRoute

    @Serializable
    @SerialName("title")
    data class Details(val id: Int, val isSerial: Boolean) : StoredRoute

    @Serializable
    @SerialName("watch")
    data class WebPlayer(
        val url: String,
        val title: String = "",
        val videoId: Int = -1,
        val isTvShow: Boolean = false,
        val seasonNumber: Int = 0,
        val episodeNumber: Int = 0,
        val imdbId: String = "",
        val imageUrl: String = "",
        val backdropUrl: String = "",
    ) : StoredRoute

    @Serializable
    @SerialName("search")
    data object Search : StoredRoute

    @Serializable
    @SerialName("profile")
    data object Profile : StoredRoute

    @Serializable
    @SerialName("login")
    data object Login : StoredRoute

    @Serializable
    @SerialName("otp")
    data class Otp(val email: String, val flowType: String) : StoredRoute

    @Serializable
    @SerialName("password-reset")
    data class ChangePassword(val token: String?) : StoredRoute

    @Serializable
    @SerialName("password-forgot")
    data class ForgotPassword(val email: String?) : StoredRoute

    @Serializable
    @SerialName("party")
    data class Decide(val partyCode: String = "") : StoredRoute

    @Serializable
    @SerialName("person")
    data class Person(val personId: Int) : StoredRoute
}

private fun Route.toStored(): StoredRoute = when (this) {
    Route.Main -> StoredRoute.Main
    Route.AppInfo -> StoredRoute.AppInfo
    Route.History -> StoredRoute.History
    is Route.AllVideo -> StoredRoute.AllVideo(screenType, title, genre, personId)
    is Route.VideoPlayer -> StoredRoute.VideoPlayer(url)
    Route.Update -> StoredRoute.Main
    is Route.Dialog.Details -> StoredRoute.Details(id, isSerial)
    is Route.Dialog.WebPlayer -> StoredRoute.WebPlayer(
        url = url,
        title = title,
        videoId = videoId,
        isTvShow = isTvShow,
        seasonNumber = seasonNumber,
        episodeNumber = episodeNumber,
        imdbId = imdbId,
        imageUrl = imageUrl,
        backdropUrl = backdropUrl,
    )
    Route.Dialog.Search -> StoredRoute.Search
    Route.Dialog.Profile -> StoredRoute.Profile
    Route.Dialog.Login -> StoredRoute.Login
    is Route.Dialog.OtpScreen -> StoredRoute.Otp(email, flowType)
    is Route.Dialog.ChangePassword -> StoredRoute.ChangePassword(token)
    is Route.Dialog.ForgotPassword -> StoredRoute.ForgotPassword(email)
    is Route.Dialog.Decide -> StoredRoute.Decide(partyCode)
    is Route.Dialog.Person -> StoredRoute.Person(personId)
}

private fun StoredRoute.toRoute(): Route = when (this) {
    StoredRoute.Main -> Route.Main
    StoredRoute.AppInfo -> Route.AppInfo
    StoredRoute.History -> Route.History
    is StoredRoute.AllVideo -> Route.AllVideo(screenType, title, genre, personId)
    is StoredRoute.VideoPlayer -> Route.VideoPlayer(url)
    is StoredRoute.Details -> Route.Dialog.Details(id, isSerial)
    is StoredRoute.WebPlayer -> Route.Dialog.WebPlayer(
        url = url.ifBlank { embedPlayUrl(imdbId, isTvShow, seasonNumber, episodeNumber) },
        title = title,
        videoId = videoId,
        isTvShow = isTvShow,
        seasonNumber = seasonNumber,
        episodeNumber = episodeNumber,
        imdbId = imdbId,
        imageUrl = imageUrl,
        backdropUrl = backdropUrl,
    )
    StoredRoute.Search -> Route.Dialog.Search
    StoredRoute.Profile -> Route.Dialog.Profile
    StoredRoute.Login -> Route.Dialog.Login
    is StoredRoute.Otp -> Route.Dialog.OtpScreen(email = email, flowType = flowType)
    is StoredRoute.ChangePassword -> Route.Dialog.ChangePassword(token)
    is StoredRoute.ForgotPassword -> Route.Dialog.ForgotPassword(email)
    is StoredRoute.Decide -> Route.Dialog.Decide(partyCode)
    is StoredRoute.Person -> Route.Dialog.Person(personId)
}

private fun Route.toBrowserPath(): String = when (this) {
    Route.Main, Route.Update -> "/"
    Route.AppInfo -> "/about"
    Route.History -> "/history"
    is Route.AllVideo -> buildString {
        append("/videos?type=")
        append(encodeQuery(screenType))
        append("&title=")
        append(encodeQuery(title))
        append("&genre=")
        append(encodeQuery(genre))
        if (personId >= 0) {
            append("&person=")
            append(personId)
        }
    }
    is Route.VideoPlayer -> "/player?url=${encodeQuery(url)}"
    is Route.Dialog.Details -> if (isSerial) "/series/$id" else "/movies/$id"
    is Route.Dialog.WebPlayer -> buildString {
        append("/watch/")
        append(videoId)
        append("?imdb=")
        append(encodeQuery(imdbId))
        append("&tv=")
        append(if (isTvShow) 1 else 0)
        append("&s=")
        append(seasonNumber)
        append("&e=")
        append(episodeNumber)
        append("&title=")
        append(encodeQuery(title))
    }
    Route.Dialog.Search -> "/search"
    Route.Dialog.Profile -> "/profile"
    Route.Dialog.Login -> "/login"
    is Route.Dialog.OtpScreen -> "/login/verify"
    is Route.Dialog.ChangePassword -> "/password/reset?token=${encodeQuery(token.orEmpty())}"
    is Route.Dialog.ForgotPassword -> "/password/forgot?email=${encodeQuery(email.orEmpty())}"
    is Route.Dialog.Decide -> {
        val code = partyCode.trim()
        if (code.isEmpty()) "/party" else "/party/${encodeQuery(code)}"
    }
    is Route.Dialog.Person -> "/person/$personId"
}

private fun decodeRoute(raw: String): Route? {
    val path = raw.substringBefore('?').let { value ->
        if (value.length > 1) value.trimEnd('/') else value.ifBlank { "/" }
    }
    val query = parseQuery(raw.substringAfter('?', ""))
    val segments = path.split('/').filter { it.isNotEmpty() }
    return when (segments.firstOrNull()) {
        null -> Route.Main
        "about" -> Route.AppInfo
        "history" -> Route.History
        "videos" -> Route.AllVideo(
            screenType = query["type"].orEmpty(),
            title = query["title"].orEmpty(),
            genre = query["genre"].orEmpty(),
            personId = query["person"]?.toIntOrNull() ?: -1,
        )
        "player" -> query["url"]?.let { Route.VideoPlayer(it) }
        "title" -> {
            val id = segments.getOrNull(1)?.toIntOrNull() ?: return null
            Route.Dialog.Details(id = id, isSerial = query["serial"] == "1")
        }
        "watch" -> {
            val videoId = segments.getOrNull(1)?.toIntOrNull() ?: -1
            val imdbId = query["imdb"].orEmpty()
            val isTvShow = query["tv"] == "1"
            val season = query["s"]?.toIntOrNull() ?: 0
            val episode = query["e"]?.toIntOrNull() ?: 0
            Route.Dialog.WebPlayer(
                url = embedPlayUrl(imdbId, isTvShow, season, episode),
                title = query["title"].orEmpty(),
                videoId = videoId,
                isTvShow = isTvShow,
                seasonNumber = season,
                episodeNumber = episode,
                imdbId = imdbId,
            )
        }
        "search" -> Route.Dialog.Search
        "profile" -> Route.Dialog.Profile
        "login" -> Route.Dialog.Login
        "password" -> when (segments.getOrNull(1)) {
            "reset" -> Route.Dialog.ChangePassword(token = query["token"])
            "forgot" -> Route.Dialog.ForgotPassword(email = query["email"])
            else -> null
        }
        "party" -> Route.Dialog.Decide(
            partyCode = segments.getOrNull(1)?.let(::decodeQuery).orEmpty(),
        )
        "person" -> {
            val id = segments.getOrNull(1)?.toIntOrNull() ?: return null
            Route.Dialog.Person(id)
        }
        else -> null
    }
}

private fun parseQuery(query: String): Map<String, String> {
    if (query.isBlank()) return emptyMap()
    return query.split('&').mapNotNull { part ->
        if (part.isEmpty()) return@mapNotNull null
        val key = decodeQuery(part.substringBefore('='))
        val value = decodeQuery(part.substringAfter('=', ""))
        key to value
    }.toMap()
}

internal fun encodeQuery(value: String): String = buildString {
    value.encodeToByteArray().forEach { byte ->
        val code = byte.toInt() and 0xFF
        val plain = code in 'a'.code..'z'.code ||
            code in 'A'.code..'Z'.code ||
            code in '0'.code..'9'.code ||
            code == '-'.code || code == '_'.code || code == '.'.code || code == '~'.code
        if (plain) {
            append(code.toChar())
        } else {
            append('%')
            append(code.toString(16).uppercase().padStart(2, '0'))
        }
    }
}

internal fun decodeQuery(value: String): String {
    val bytes = ArrayList<Byte>(value.length)
    var index = 0
    while (index < value.length) {
        val char = value[index]
        if (char == '%' && index + 2 < value.length) {
            val parsed = value.substring(index + 1, index + 3).toIntOrNull(16)
            if (parsed != null) {
                bytes.add(parsed.toByte())
                index += 3
                continue
            }
        }
        if (char == '+') {
            bytes.add(' '.code.toByte())
        } else {
            char.toString().encodeToByteArray().forEach { bytes.add(it) }
        }
        index++
    }
    return bytes.toByteArray().decodeToString()
}

@Suppress("RestrictedApi")
internal fun NavBackStackEntry.appRoute(): Route? {
    val destination = destination
    if (destination.route == "empty") return null
    return when {
        destination.hasRoute<Route.Main>() -> Route.Main
        destination.hasRoute<Route.AppInfo>() -> Route.AppInfo
        destination.hasRoute<Route.History>() -> Route.History
        destination.hasRoute<Route.Update>() -> Route.Update
        destination.hasRoute<Route.AllVideo>() -> toRoute<Route.AllVideo>()
        destination.hasRoute<Route.VideoPlayer>() -> toRoute<Route.VideoPlayer>()
        destination.hasRoute<Route.Dialog.Details>() -> toRoute<Route.Dialog.Details>()
        destination.hasRoute<Route.Dialog.WebPlayer>() -> toRoute<Route.Dialog.WebPlayer>()
        destination.hasRoute<Route.Dialog.Search>() -> Route.Dialog.Search
        destination.hasRoute<Route.Dialog.Profile>() -> Route.Dialog.Profile
        destination.hasRoute<Route.Dialog.Login>() -> Route.Dialog.Login
        destination.hasRoute<Route.Dialog.OtpScreen>() -> toRoute<Route.Dialog.OtpScreen>()
        destination.hasRoute<Route.Dialog.ChangePassword>() -> toRoute<Route.Dialog.ChangePassword>()
        destination.hasRoute<Route.Dialog.ForgotPassword>() -> toRoute<Route.Dialog.ForgotPassword>()
        destination.hasRoute<Route.Dialog.Decide>() -> toRoute<Route.Dialog.Decide>()
        destination.hasRoute<Route.Dialog.Person>() -> toRoute<Route.Dialog.Person>()
        else -> null
    }
}
